import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators, EmailValidator } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Address } from 'src/app/models/address.model';
import { CompanyInfo } from 'src/app/models/companyinfo.model';
import { Config } from 'src/app/models/config.model';
import { Country } from 'src/app/models/country.model';
import { SaveCaseInput } from 'src/app/models/save-case-input.model';
import { UploadFile } from 'src/app/models/uploadfile.model';
import { UserInfo } from 'src/app/models/user-info.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { CaseService } from 'src/app/services/case.service';
import { ConfigService } from 'src/app/services/config.service';
import { ContentService } from 'src/app/services/content.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { TitleService } from 'src/app/services/title.service';
import { UploadService } from 'src/app/services/upload.service';
import { companyValidator } from 'src/app/validators/company.validator';
import { emailValidator, multipleEmailValidator } from 'src/app/validators/email.validator';
import { orgnumberFormatValidator } from 'src/app/validators/orgnumber.validator';
import { phoneValidator } from 'src/app/validators/phone.validator';
import { postalCodeValidator } from 'src/app/validators/postalcode.validator';
import { FileUploadComponent } from '../../file-upload/file-upload.component';
import { SnackbarMessageComponent } from '../../snackbar/snackbar-message/snackbar-message.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IContentComponent } from 'src/app/interfaces/content-component.interface';
import { CanComponentDeactivate } from 'src/app/guards/case-changed.guard';
import { ConfirmBoxComponent, DialogData } from '../../dialog/confirm-box/confirm-box.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SaveCase } from 'src/app/models/save-case.model';
import { ComplainerModel } from 'src/app/models/complainer.model';
import { LetterAttachmentModel } from 'src/app/models/letter-attachment.model';
import { equalFieldValidator } from 'src/app/validators/equal-field.validator';
import { SSNValidator } from 'src/app/validators/SSN.validator';

@Component({
  selector: 'app-complaint-form',
  templateUrl: './complaint-form.component.html',
  styleUrls: ['./complaint-form.component.scss']
})
export class ComplaintFormComponent implements OnInit, OnDestroy, CanComponentDeactivate, IContentComponent {

  private componentDestroyed = new Subject();

  @ViewChild(FileUploadComponent, { static: false }) fileUploadComponent!: FileUploadComponent;

  get files(): UploadFile[] {
    if (this.fileUploadComponent) {
      return this.fileUploadComponent.files;
    } else {
      return [];
    }
  }

  get userInfo(): UserInfo | null {
    return this.authenticationService.userInfo;
  }

  pageComponentName: string = 'NewCase';
  data!: Map<string, string>;


  complaintType!: number;
  attemptResolved!: number;
  attemptSettlement!: number;

  isLoading: boolean = true;
  isUploading: boolean = false;

  caseForm!: FormGroup;

  companies$!: Observable<any>;

  config!: Config;
  filteredCountries!: Observable<Country[]>;

  companyInfo: CompanyInfo | null = null;
  complainerCompanyInfo: CompanyInfo | null = null;

  constructor(
    private caseService: CaseService,
    private authenticationService: AuthenticationService,
    private contentService: ContentService,
    private configService: ConfigService,
    private spinnerService: SpinnerService,
    private router: Router,
    private route: ActivatedRoute,
    private snackbar: MatSnackBar,
    private matDialog: MatDialog,
    private uploadService: UploadService,
    private titleService: TitleService) {

    this.titleService.setTitle("Finansklagenemnda - Klageskjema");

    this.contentService.content
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (data: Map<string, string>) => {
          if (data !== undefined) {
            this.data = data;
          }
        }
      );

    this.contentService.loadContent('pageText', this.pageComponentName, ['text', 'label']);
  }

  ngOnInit(): void {

    this.configService.config
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (c: Config | null) => {
          if (c !== null) {
            this.config = c;
            this.filteredCountries = this.getFilteredCountries();
          }
        }
      );

    this.uploadService.isUploading
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: boolean) => {
          this.isUploading = value;
        }
      );

    this.companies$ = this.configService.companies;

    this.route.params.pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (params: Params) => {

          this.attemptResolved = +params['attemptResolved']; // (+) converts string 'id' to a number
          this.attemptSettlement = +params['attemptSettlement']; // (+) converts string 'id' to a number
          this.complaintType = +params['complaintType']; // (+) converts string 'id' to a number
          this.configService.loadCompanies(this.complaintType);

          this.createForms();
          this.initYouForm(false);
          this.isLoading = false;
        }
      );
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next(null);
    this.componentDestroyed.unsubscribe();
  }

  getLabelValue(label: string): string {
    let value: string = '';
    if (this.data) {
      value = this.data.get(label) ?? '';
    }
    return value;
  }

  companyInfoReceived(event: CompanyInfo | null) {
    this.companyInfo = event;
    this.initYouForm(false);
  }

  complainerCompanyInfoReceived(event: CompanyInfo | null) {
    this.complainerCompanyInfo = event;
    this.initComplainerForm(false);
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {

    var changes: boolean = this.caseForm.dirty && this.caseForm.touched;

    if (!changes) {
      changes = this.files.length > 0;
    }

    if (changes) {

      let initialState: DialogData = {
        body: 'Advarsel: Innholdet er endret. Hvis du forlater siden, vil du miste endringene.',
        title: 'Forlate siden?',
        okBtnName: 'Ok',
        closeBtnName: 'Avbryt'
      };

      const dialogRef: MatDialogRef<ConfirmBoxComponent, boolean> = this.matDialog.open(ConfirmBoxComponent, { data: initialState })      
      return dialogRef.afterClosed()
        .pipe(
          map(
            (result: boolean | undefined) => {
              
              if (result === undefined) {
                result = false;
              }
              if (result === true) {
                console.log('clearing files')
                this.uploadService.clearFiles(true, false);
              }
              return result;
            }
          )
        )
    }
    else {
      return true;
    }
  }

  createForms(): void {

    this.caseForm = new FormGroup({
      onBehalfOfForm: this.createBehalfOfForm(),
      youForm: this.createYouForm(),
      complainerForm: this.createComplainerForm(),
      companyForm: this.createCompanyForm(),
      descriptionForm: this.createDescriptionForm(),
      otherAddress: new FormControl(null),
      otherEmail: new FormControl({ value: new Date().toISOString(), disabled: true })
    });

  }
  createBehalfOfForm(): FormGroup {
    let form = new FormGroup({
      onBehalfOf: new FormControl<number>(1, [Validators.required])
    });

    form.controls.onBehalfOf.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: number | null) => {

          this.initYouForm(true);

          if (this.complainerHasCorrespondent()) {
            let complainerForm: FormGroup | null = this.getFormGroup('complainerForm');
            if (complainerForm !== null) {
              complainerForm.controls.complainerIsCompany.setValue(1, { emitEvent: false });
            }
            this.initComplainerForm(true);
          }
          else {
            this.caseForm.controls.complainerForm = this.createComplainerForm();
          }
        }
      );

    return form;

  }
  createYouForm(): FormGroup {

    let form = new FormGroup({
      orgNumber: new FormControl<string|null>(null),
      firstName:  new FormControl<string|null>(null, [Validators.required, Validators.maxLength(100)]),
      lastName:  new FormControl<string|null>(null, [Validators.required, Validators.maxLength(100)]),
      address: new FormControl<string|null>(null, [Validators.required, Validators.maxLength(200)]),
      zipCode: new FormControl<string|null>(null, [Validators.required, postalCodeValidator()]),
      zipArea: new FormControl<string|null>({value: null, disabled: true }),
      countryCodeAlpha2: new FormControl<string|null>('NO'),
      telephone: new FormControl<string|null>(null),
      telephoneCountryCodeAlpha2: new FormControl<string|null>('NO'),
      email: new FormControl<string|null>(null, [Validators.required, multipleEmailValidator(), Validators.maxLength(100)]),
      confirmEmail: new FormControl<string|null>(null, Validators.required),
      reference: new FormControl<string | null>(null)
    })

    form.controls.telephone.setValidators([Validators.required,phoneValidator(form.controls.telephoneCountryCodeAlpha2)]);
    form.controls.email.addValidators([emailValidator(form.controls.confirmEmail)]);

    form.controls.confirmEmail.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: string | null) => {
          form.controls.email.updateValueAndValidity();
        }
      )

    form.controls.zipCode.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (val: string | null) => {
          if (val !== undefined && val !== null && val.length === 4) {
            this.caseService.loadPostalPlace(val)
              .pipe(takeUntil(this.componentDestroyed))
              .subscribe(
                (value: string) => {
                  if (value === 'Ugyldig postnummer') {
                    this.getFormGroup('youForm')?.controls.zipArea.setValue(null);
                  } else {
                    this.getFormGroup('youForm')?.controls.zipArea.setValue(value);
                  }
                }
              );
          } else {
            this.getFormGroup('youForm')?.controls.zipArea.setValue(null);
          }
        }
      )

    form.controls.telephoneCountryCodeAlpha2.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: string | null) => {
          form.controls.telephone.updateValueAndValidity(); //check if valid with new country code
        }
      )

    form.controls.countryCodeAlpha2.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: string | null) => {
          form.controls.telephoneCountryCodeAlpha2.setValue(value);
          form.controls.telephone.setValue(null, { emitEvent: false });

          if (value == 'NO') {
            form.controls.zipCode.addValidators([Validators.required, postalCodeValidator()]);
            this.setYouFormAddressValues(form);
          }
          else {
            form.controls.address.setValue(null, { emitEvent: false });
            form.controls.zipArea.setValue(null, { emitEvent: false });
            form.controls.zipCode.setValue(null, { emitEvent: false });
            form.controls.zipCode.clearValidators();
          }

          form.controls.zipCode.updateValueAndValidity();
        }
      )

    return form;
  }
  createComplainerForm(): FormGroup {

    let form = new FormGroup({
      complainerIsCompany: new FormControl<number>(1),
      pnr: new FormControl<string|null>(null, [SSNValidator()]),
      orgNumber: new FormControl<string|null>(null),
      firstName: new FormControl<string|null>(null, [Validators.maxLength(100)]),
      lastName: new FormControl<string|null>(null, [Validators.maxLength(100)]),
      address: new FormControl<string|null>(null, [Validators.maxLength(200)]),
      zipCode: new FormControl<string|null>(null, [postalCodeValidator()]),
      zipArea: new FormControl<string|null>({ value: null, disabled: true }),
      countryCodeAlpha2: new FormControl<string|null>('NO'),
      telephone: new FormControl<string|null>(null),
      telephoneCountryCodeAlpha2: new FormControl<string|null>('NO'),
      email: new FormControl<string|null>(null, [multipleEmailValidator(), Validators.maxLength(100)]),
    });

    form.controls.zipCode.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (val: string | null) => {

          if (val !== null && val.length === 4) {
            this.caseService.loadPostalPlace(val)
              .pipe(takeUntil(this.componentDestroyed))
              .subscribe(
                (value: string) => {
                  if (value === 'Ugyldig postnummer') {
                    form.controls.zipArea.setValue(null);
                  } else {
                    form.controls.zipArea.setValue(value);
                  }
                }
              );
          } else {
            form.controls.zipArea.setValue(null);
          }


        })

    form.controls.complainerIsCompany.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (val: number | null) => {
          this.initComplainerForm(true);
        }
      );

    form.controls.telephoneCountryCodeAlpha2.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: string | null) => {
          form.controls.telephone.updateValueAndValidity(); //check if valid with new country code
        }
      )

    form.controls.countryCodeAlpha2.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: string | null) => {
          form.controls.telephoneCountryCodeAlpha2.setValue(value);
          form.controls.telephone.setValue(null, { emitEvent: false });

          if (value == 'NO') {
            form.controls.zipCode.addValidators([postalCodeValidator()]);
            this.setComplainerFormValues(form);
          }
          else {
            form.controls.address.setValue(null, { emitEvent: false });
            form.controls.zipArea.setValue(null, { emitEvent: false });
            form.controls.zipCode.setValue(null, { emitEvent: false });
            form.controls.zipCode.clearValidators();
          }

          form.controls.zipCode.updateValueAndValidity();
        }
      )

    return form;
  }
  createCompanyForm(): FormGroup {
    let form = new FormGroup({
      selectedCompany: new FormControl<number|null>(-1),
      otherCompanyName: new FormControl(null, [Validators.maxLength(50)]),
      companyCaseRef: new FormControl(null, [Validators.maxLength(50)]),
      contactPersonCompany: new FormControl(null, [Validators.maxLength(50)]),
    });

    form.controls.selectedCompany.setValidators(companyValidator(form.controls.otherCompanyName));

    if (this.complaintType !== 0) {
      form.controls.companyCaseRef.setValidators([Validators.required, Validators.maxLength(50)]);
    }

    form.controls.otherCompanyName.valueChanges
      .subscribe(
        (value: string | null) => {
          form.controls.selectedCompany.updateValueAndValidity();
        }
      );

    return form;
  }
  createDescriptionForm(): FormGroup {
    return new FormGroup({
      letterContent: new FormControl<string|null>(null, [Validators.required, Validators.maxLength(3000)]),
    });
  }

  initYouForm(reset: boolean): void {

    if (reset) {
      this.companyInfo = null;
    }

    var youForm: FormGroup | null = this.getFormGroup('youForm');
    if (youForm !== null) {

      //setup name and orgnr values      
      if (this.isCorrespondentOrganization()) {
        youForm.controls.orgNumber.setValidators([Validators.required, orgnumberFormatValidator()]);
        youForm.controls.orgNumber.updateValueAndValidity();

        youForm.controls.orgNumber.setValue(this.companyInfo ? this.companyInfo.OrgNumber : null);
        youForm.controls.firstName.setValue(this.companyInfo ? this.companyInfo.Name : null);
        youForm.controls.lastName.setValue(this.userInfo ? this.userInfo.Fornavn + ' ' + this.userInfo.Etternavn : (this.companyInfo && this.companyInfo.ContactPersonName ? this.companyInfo.ContactPersonName : null));

        if (this.companyInfo !== null) {
          youForm.controls.orgNumber.disable({ emitEvent: false });
          youForm.controls.firstName.disable({ emitEvent: false });
        } else {
          youForm.controls.orgNumber.enable({ emitEvent: false });
          youForm.controls.firstName.enable({ emitEvent: false });
        }

        if (this.userInfo !== null) {
          youForm.controls.lastName.disable({ emitEvent: false });
        } else {
          youForm.controls.lastName.enable({ emitEvent: false });
        }
      }
      else {
        youForm.controls.orgNumber.clearValidators();
        youForm.controls.orgNumber.updateValueAndValidity();

        youForm.controls.firstName.setValue(this.userInfo ? this.userInfo.Fornavn : null);
        youForm.controls.lastName.setValue(this.userInfo ? this.userInfo.Etternavn : null);

        if (this.userInfo !== null) {
          youForm.controls.firstName.disable();
          youForm.controls.lastName.disable();
        } else {
          youForm.controls.firstName.enable();
          youForm.controls.lastName.enable();
        }
      }

      this.setYouFormAddressValues(youForm);

      if (reset) {
        youForm.controls.telephoneCountryCodeAlpha2.setValue('NO', { emitEvent: false });
        youForm.controls.telephone.setValue(null, { emitEvent: false });
        youForm.controls.email.setValue(null, { emitEvent: false });
        youForm.controls.confirmEmail.setValue(null, { emitEvent: false });
        //youForm.controls.reference.setValue(null, { emitEvent: false });
      }
    }
  }
  setYouFormAddressValues(youForm: FormGroup): void {

    if (this.companyInfo !== null) {
      var addressValue: Address | null = this.companyInfo.PostalAddress !== null && this.companyInfo.PostalAddress.Address !== null ? this.companyInfo.PostalAddress : this.companyInfo.BusinessAddress;
      if (addressValue !== null) {
        var countryCode: string | null = addressValue.Country_code ? addressValue.Country_code : null;
        var address: string | null = addressValue.Address;
        var postalCode: string | null = addressValue.PostalCode;
        var postalPlace: string | null = addressValue.PostalPlace;

        youForm.controls.address.setValue(address, { emitEvent: false });
        //youForm.controls.address.disable({ emitEvent: false });

        youForm.controls.zipCode.setValue(postalCode, { emitEvent: false });
        //youForm.controls.zipCode.disable({ emitEvent: false });

        youForm.controls.zipArea.setValue(postalPlace, { emitEvent: false });
        youForm.controls.zipArea.disable({ emitEvent: false });

        if (countryCode !== null) {
          let country: Country | undefined;
          if (countryCode.length === 2) {
            country = this.config.Countries.find(x => x.cca2 === countryCode);
          } else if (countryCode.length === 3) {
            country = this.config.Countries.find(x => x.cca3 === countryCode);
          }

          if (country !== undefined) {
            youForm.controls.countryCodeAlpha2.setValue(country.cca2, { emitEvent: false });
          }
        }
      }
    } else {
      youForm.controls.address.setValue(this.userInfo ? this.userInfo.Adresse : null, { emitEvent: false });
      youForm.controls.zipCode.setValue(this.userInfo ? this.userInfo.Postnr : null, { emitEvent: false });
      youForm.controls.zipArea.setValue(this.userInfo ? this.userInfo.Poststed : null, { emitEvent: false });
      youForm.controls.countryCodeAlpha2.setValue('NO', { emitEvent: false });
    }
  }

  initComplainerForm(reset: boolean): void {

    if (reset) {
      this.complainerCompanyInfo = null;
    }

    var complainerForm: FormGroup | null = this.getFormGroup('complainerForm');
    if (complainerForm !== null) {
  
      let isCompanyVal: number = complainerForm.controls.complainerIsCompany.value;
      if (isCompanyVal === 1) {

        complainerForm.controls.orgNumber.setValue(null, { emitEvent: false });
        complainerForm.controls.orgNumber.clearValidators();
        complainerForm.controls.orgNumber.updateValueAndValidity();

        complainerForm.controls.firstName.setValue(null, { emitEvent: false });
        complainerForm.controls.firstName.setValidators([Validators.required, Validators.maxLength(100)]);
        complainerForm.controls.firstName.updateValueAndValidity();
        complainerForm.controls.firstName.enable({ emitEvent: false });

        complainerForm.controls.lastName.setValue(null, { emitEvent: false });
        complainerForm.controls.lastName.setValidators([Validators.required, Validators.maxLength(100)]);
        complainerForm.controls.lastName.updateValueAndValidity();
        complainerForm.controls.lastName.enable({ emitEvent: false });
      }
      else if (isCompanyVal === 2) {

        complainerForm.controls.orgNumber.setValue(this.complainerCompanyInfo ? this.complainerCompanyInfo.OrgNumber : null);
        complainerForm.controls.orgNumber.setValidators([Validators.required, orgnumberFormatValidator()]);
        complainerForm.controls.orgNumber.updateValueAndValidity();

        complainerForm.controls.firstName.setValue(this.complainerCompanyInfo ? this.complainerCompanyInfo.Name : null);
        complainerForm.controls.firstName.setValidators([Validators.required, Validators.maxLength(100)]);
        complainerForm.controls.firstName.updateValueAndValidity();

        complainerForm.controls.lastName.setValue(this.complainerCompanyInfo && this.complainerCompanyInfo.ContactPersonName ? this.complainerCompanyInfo.ContactPersonName : null);
        complainerForm.controls.lastName.setValidators([Validators.maxLength(100)]);
        complainerForm.controls.lastName.updateValueAndValidity();
      }

      complainerForm.controls.email.setValue(null, { emitEvent: false });
      complainerForm.controls.email.setValidators([Validators.required, multipleEmailValidator(), Validators.maxLength(100), 
        equalFieldValidator((this.caseForm.controls.youForm as FormGroup).controls.email)]);


      complainerForm.controls.email.updateValueAndValidity();
      complainerForm.controls.email.enable({ emitEvent: false });
      
      complainerForm.controls.telephone.setValue(null, { emitEvent: false });
      complainerForm.controls.telephone.setValidators([Validators.required, phoneValidator(complainerForm.controls.telephoneCountryCodeAlpha2), 
        equalFieldValidator((this.caseForm.controls.youForm as FormGroup).controls.telephone)]);

      complainerForm.controls.telephone.updateValueAndValidity();
      complainerForm.controls.telephone.enable({ emitEvent: false });

      if (this.complainerCompanyInfo !== null) {
        complainerForm.controls.orgNumber.disable({ emitEvent: false });
        complainerForm.controls.firstName.disable({ emitEvent: false });
      }
      else {
        complainerForm.controls.orgNumber.enable({ emitEvent: false });
        complainerForm.controls.firstName.enable({ emitEvent: false });
      }

      this.setComplainerFormValues(complainerForm);

      if (reset) {
        complainerForm.controls.telephoneCountryCodeAlpha2.setValue('NO', { emitEvent: false });
        complainerForm.controls.telephone.setValue(null, { emitEvent: false });
        complainerForm.controls.email.setValue(null, { emitEvent: false });
      }

    }
  }
  setComplainerFormValues(complainerForm: FormGroup): void {

    let isCompanyVal: number = complainerForm.controls.complainerIsCompany.value;

    if (isCompanyVal === 2 && this.complainerCompanyInfo !== null) {
      var addressValue: Address | null = this.complainerCompanyInfo.PostalAddress !== null && this.complainerCompanyInfo.PostalAddress.Address !== null ? this.complainerCompanyInfo.PostalAddress : this.complainerCompanyInfo.BusinessAddress;
      if (addressValue !== null) {
        var countryCode: string | null = addressValue.Country_code ? addressValue.Country_code : null;
        var address: string | null = addressValue.Address;
        var postalCode: string | null = addressValue.PostalCode;
        var postalPlace: string | null = addressValue.PostalPlace;

        complainerForm.controls.address.setValue(address, { emitEvent: false });
        complainerForm.controls.zipCode.setValue(postalCode, { emitEvent: false });
        complainerForm.controls.zipArea.setValue(postalPlace, { emitEvent: false });

        if (countryCode !== null) {
          let country: Country | undefined;
          if (countryCode.length === 2) {
            country = this.config.Countries.find(x => x.cca2 === countryCode);
          } else if (countryCode.length === 3) {
            country = this.config.Countries.find(x => x.cca3 === countryCode);
          }

          if (country !== undefined) {
            complainerForm.controls.countryCodeAlpha2.setValue(country.cca2, { emitEvent: false });
          }
        }
      }
    } else {
      complainerForm.controls.address.setValue(null, { emitEvent: false });

      complainerForm.controls.address.enable({ emitEvent: false });

      complainerForm.controls.zipCode.setValue(null, { emitEvent: false });
      complainerForm.controls.zipCode.enable({ emitEvent: false });

      complainerForm.controls.zipArea.setValue(null, { emitEvent: false });

      complainerForm.controls.countryCodeAlpha2.setValue('NO', { emitEvent: false });
      complainerForm.controls.countryCodeAlpha2.enable({ emitEvent: false });
    }

  }

  getFormGroup(formGroupName: string) {
    if (this.caseForm) {
      return (this.caseForm.get(formGroupName) as FormGroup);
    }
    return null;
  }
  getFilteredCountries(): Observable<Country[]> {
    //var codes = this.config.Countries.filter(z => z.region.toLowerCase() == 'europe').filter(x => x.callingcodes && x.callingcodes.length > 0)
    var codes = this.config.Countries.filter(x => x.callingcodes && x.callingcodes.length > 0);
    return of(codes);
  }
  getComplainerTypeText(): string {

    switch (this.complaintType) {
      case 0: {
        return 'bank og finans';
      }
      case 1: {
        return 'inkasso';
      }
      case 2: {
        return 'personforsikring';
      }
      case 3: {
        return 'skadeforsikring';
      }
      case 4: {
        return 'kollisjon';
      }
      case 5: {
        return 'eierskifte';
      }
      default: {
        return '';
      }
    }
  }
  getDisplayValueFromCountryCode(cca2: string): string {
    var displayValue: string = '';
    var country: Country | undefined = this.config?.Countries.find(c => c.cca2 === cca2);
    if (country) {
      displayValue = country.name.common;
    }
    return displayValue;
  }
  getPhoneDisplayValueFromCountryCode(countryCode: string, withName: boolean): string {
    var displayValue: string = '';

    if (countryCode && countryCode !== '') {
      var country: Country | undefined = this.config?.Countries.find(c => c.cca2 === countryCode);
      if (country) {
        if (country.callingcodes && country.callingcodes.length > 0) {
          displayValue = country.callingcodes[0];
        }
        if (withName) {
          displayValue = country.name.common + ' (' + displayValue + ')';
        }
      }
    }

    return displayValue;
  }
  getAlternativeAutocomplete(defaultLabel: string, isComplainerForm: boolean): string {

    var label = defaultLabel;
    let value = isComplainerForm ?
      this.getFormGroup('complainerForm')?.controls.complainerIsCompany.value :
      this.getFormGroup('onBehalfOfForm')?.controls.onBehalfOf.value;

    switch (defaultLabel) {
      case 'given-name': {

        if (isComplainerForm) {
          if (value === 2) {
            label = 'organization';
          }
        } else {
          if (value === 3 || value === 4) {
            label = 'organization';
          }
        }

        break;
      }
      case 'family-name': {
        if (isComplainerForm) {
          label = 'name'
        } else {
          if (value === 3 || value === 4) {
            label = 'name'; //fullt navn på kontaktperson          
          }
        }

        break
      }
      default: {
        return defaultLabel;
      }
    }

    return label;
  }
  getAlternativePlaceholder(defaultLabel: string, isComplainerForm: boolean): string {

    var label = defaultLabel;
    let value = isComplainerForm ?
      this.getFormGroup('complainerForm')?.controls.complainerIsCompany.value :
      this.getFormGroup('onBehalfOfForm')?.controls.onBehalfOf.value;

    switch (defaultLabel) {
      case 'Fornavn': {
        if (isComplainerForm) {
          if (value === 2) {
            label = 'Firma';
          }
        } else {
          if (value === 3 || value === 4) {
            label = 'Firma';
          }
        }

        break;
      }
      case 'Etternavn': {
        if (isComplainerForm) {
          if (value === 2) {
            label = 'Representant';
          }
        } else {
          if (value === 3 || value === 4) {
            label = 'Att';
          }
        }
        break
      }
      default: {
        return defaultLabel;
      }
    }

    return label;
  }

  submitForm(): void {

    if (!this.caseForm.valid) {
      this.snackbar.openFromComponent(SnackbarMessageComponent, { data: { message: 'Du har ikke fylt ut skjemaet riktig, gå tilbake og sjekk opplysningene', type: 'warning' } });
      return;
    }

    if (this.caseForm.controls.otherAddress.value !== null && this.caseForm.controls.otherAddress.value !== '') {
      return;
    }

    let youForm: FormGroup | null = this.getFormGroup('youForm');
    if (youForm !== null) {

      let you: ComplainerModel = {
        FirstName: youForm.controls.firstName.value,
        LastName: youForm.controls.lastName.value,
        Address: {
          Address: youForm.controls.address.value,
          PostalCode: youForm.controls.zipCode.value,
          PostalPlace: youForm.controls.zipArea.value,
          CountryCodeAlpha2: youForm.controls.countryCodeAlpha2.value
        },
        Phone: {
          Phone: youForm.controls.telephone.value,
          CountryCodeAlpha2: youForm.controls.telephoneCountryCodeAlpha2.value
        },
        Email: youForm.controls.email.value,
        Orgnr: youForm.controls.orgNumber.value,
        Reference: youForm.controls.reference.value,
        IsFirm: this.isCorrespondentOrganization(),
      };

      let representing: ComplainerModel | null = null;
      let complainerIsCompany: boolean = false;

      if (this.complainerHasCorrespondent()) {
        let complainerForm: FormGroup | null = this.getFormGroup('complainerForm');
        if (complainerForm !== null) {

          representing = {
            FirstName: complainerForm.controls.firstName.value,
            LastName: complainerForm.controls.lastName.value,
            Address: {
              Address: complainerForm.controls.address.value,
              PostalCode: complainerForm.controls.zipCode.value,
              PostalPlace: complainerForm.controls.zipArea.value,
              CountryCodeAlpha2: complainerForm.controls.countryCodeAlpha2.value
            },
            Phone: {
              Phone: complainerForm.controls.telephone.value,
              CountryCodeAlpha2: complainerForm.controls.telephoneCountryCodeAlpha2.value
            },
            Email: complainerForm.controls.email.value,
            Orgnr: complainerForm.controls.orgNumber.value,
            Reference: null,
            CPNr: complainerForm.controls.pnr.value,
            IsFirm: complainerForm.controls.complainerIsCompany.value === 2
          };

          complainerIsCompany = complainerForm.controls.complainerIsCompany.value === 2;
        }
      }

      let onBehalfOfForm: FormGroup | null = this.getFormGroup('onBehalfOfForm');
      if (onBehalfOfForm !== null) {
        let newCase: SaveCase = {
          OnBehalfOf: onBehalfOfForm.controls.onBehalfOf.value,
          You: you,
          RepresentingOnBehalfOf: representing,

          ComplainerIsCompany: complainerIsCompany,

          AddressType: 0, //this.form.controls.addressType.value,
          ComplaintType: this.complaintType,

          CompanyId: this.getFormGroup('companyForm')?.controls.selectedCompany.value.Id, // velges fra select
          OtherCompanyName: this.getFormGroup('companyForm')?.controls.otherCompanyName.value, // selectedCompany.value.CompanyName,
          CompanyCaseRef: this.getFormGroup('companyForm')?.controls.companyCaseRef.value,
          ContactPersonCompany: this.getFormGroup('companyForm')?.controls.contactPersonCompany.value,

          TriedSolved: this.attemptResolved,
          BroughtToBailiff: this.attemptSettlement,

          // både beskrivelse og filer
          Letter: {
            Subject: '',
            Content: this.getFormGroup('descriptionForm')?.controls.letterContent.value,
            Files: []
          },
        };

        if (this.files.length > 0) {
          this.files.forEach(item => {

            if (item.deleteKey) {
              let letterCaseAttachment: LetterAttachmentModel = {
                Filename: item.deleteKey.Filename,
                Key: item.deleteKey?.Key
              }

              newCase.Letter.Files.push(letterCaseAttachment);
            }
          });
        }

        let inputCase: SaveCaseInput = {
          Case: newCase,
          OtherAddress: this.caseForm.controls.otherAddress.value,
          OtherEmail: this.caseForm.controls.otherEmail.value
        }

        this.spinnerService.show('Sender klage, vent litt...');

        this.caseService.saveNewCase(inputCase)
          .subscribe(
            {
              next: (savedCase: SaveCase) => {
                this.fileUploadComponent.clearFiles(false, false);
                this.caseForm.markAsPristine();
  
                this.router.navigate(
                  ['form', 'feedback'],
                  { queryParams: { c: savedCase.CaseUid } }
                );
                //this.router.navigateByUrl('/form/feedback?' + savedCase.CaseUid);
              },
              error: (error: HttpErrorResponse) => {
                console.log(error);
                this.spinnerService.hide();
              },
              complete: () => {
                console.log('Finished saving new case');
                this.spinnerService.hide();
              }
  
            }
          );
      }
    }
  }

  openFile(file: UploadFile): void {
    this.fileUploadComponent.openFile(file);
  }

  splitNewLine(input: string): string[] {
    if (input === null || input === undefined || input === '') {
      return [];
    }

    return input.split(/\r?\n/);
  }

  onFileDropped(files: FileList) {

    if (this.isUploading) {
      return;
    }

    if (files.length > 0) {
      this.uploadService.addFiles(files);
    }
  }

  complainerHasCorrespondent(): boolean {
    let onBehalfOfForm: FormGroup | null = this.getFormGroup('onBehalfOfForm');
    if (onBehalfOfForm !== null) {
      let onBehalfOfVal: number = onBehalfOfForm.controls.onBehalfOf.value;
      return onBehalfOfVal === 2 || onBehalfOfVal === 3;
    }
    return false;
  }

  isCorrespondentOrganization(): boolean {
    let onBehalfOfForm: FormGroup | null = this.getFormGroup('onBehalfOfForm');
    if (onBehalfOfForm !== null) {
      let onBehalfOfVal: number = onBehalfOfForm.controls.onBehalfOf.value;
      return onBehalfOfVal === 3 || onBehalfOfVal === 4;
    }
    return false;
  }

}

